import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Helmet, HelmetProvider } from 'react-helmet-async';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Search, CheckCircle2, AlertCircle, XCircle, Info, Globe, RefreshCw, Zap, Shield, TrendingUp, Image as ImageIcon, ChevronDown, ChevronUp, ExternalLink, ClipboardCheck, Timer, Mail, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { diagnoseApi, DiagnoseJobResponse, DiagnoseImageResponse, DiagnoseA11yResponse, DiagnoseScoreDetailsResponse, DiagnoseSeoScoreDetail, DiagnoseA11yScoreDetail, NotCheckedGuide, DiagnoseStatusResponse } from '@/api/diagnoseApi';
import type { CategorySummary, DiagnoseSeoResponse } from '@/api/diagnoseApi';
import { planApi } from '@/api/planApi';
import { recordFunnelEvent } from '@/api/funnelEventApi';
import { TurnstileWidget, TURNSTILE_ENABLED } from '@/components/common/TurnstileWidget';
import { cn } from '@/lib/utils';
import { LoadingBlock, Spinner } from '@/components/ui/spinner';

// Patch: Locally extend DiagnoseSummary type to include deductionSummary and tabCounts for TS compatibility
type DiagnoseSummaryPatched = {
  totalImages: number;
  imagesWithAlt: number;
  imagesMissingAlt: number;
  fixableByAlttext: number;
  impactMessage: string;
  a11yIssueCount: number;
  seoIssueCount: number;
  tabCounts?: {
    images: number;
    a11y: number;
    seo: number;
  };
  deductionSummary?: {
    a11y: number;
    seo: number;
  };
};

// ─── [복구] 진단 페이지 상수/유틸/컴포넌트 ──────────────────────────────

// 진단 단계
type Phase = 'input' | 'loading' | 'done' | 'failed';

// 세션 키 생성기
const GT_KEY = (jobId: string) => `diagnose_guest_token_${jobId}`;
const PENDING_CLAIM_KEY = 'diagnose_pending_claim';
const CURRENT_DIAGNOSIS_KEY = 'diagnose_current_job'; // 진단 중 복구용

// 로딩 단계별 안내
const LOADING_STEPS = [
  { label: '사이트 분석 준비', icon: Globe },
  { label: '이미지 수집', icon: ImageIcon },
  { label: '접근성 검사', icon: Shield },
  { label: 'SEO 분석', icon: TrendingUp },
  { label: 'AI 품질 평가', icon: Zap },
  { label: '결과 정리', icon: CheckCircle2 },
];

// 점수 색상
function scoreColor(score: number | null | undefined): string {
  if (score == null) return '#94a3b8'; // slate-400
  if (score >= 90) return '#16a34a'; // green-600
  if (score >= 70) return '#f59e42'; // orange-500
  if (score >= 50) return '#eab308'; // yellow-600
  return '#dc2626'; // red-600
}

// 점수 라벨
function scoreLabel(score: number | null | undefined): string {
  if (score == null) return '—';
  if (score >= 90) return '매우 우수';
  if (score >= 70) return '양호';
  if (score >= 50) return '보통';
  return '미흡';
}

// 에러 메시지 매핑
function getErrorMessage(code: string): string {
  switch (code) {
    case 'RATE_LIMIT_EXCEEDED': return '진단 가능 횟수를 초과했습니다. 내일 다시 시도해 주세요.';
    case 'GLOBAL_RATE_LIMIT_EXCEEDED': return '오늘 무료 진단 이용 가능 건수를 모두 소진했습니다. 내일 다시 이용해 주세요.';
    case 'URL_ACCESS_DENIED': return '사이트에 접근할 수 없습니다. robots.txt 또는 방화벽에 의해 차단되었을 수 있습니다.';
    case 'URL_INVALID': return '유효하지 않은 URL입니다. https://로 시작하는 주소를 입력해 주세요.';
    case 'JOB_NOT_FOUND': return '진단 결과를 찾을 수 없습니다.';
    case 'JOB_AUTO_CREATED': return '진단이 자동으로 재시작되었습니다.';
    case 'DIAGNOSIS_FAILED': return '진단 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
    case 'UNEXPECTED_ERROR': return '예상치 못한 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
    default: return '알 수 없는 오류가 발생했습니다.';
  }
}

/** B/E가 message 필드에 에러코드 문자열을 그대로 내려보내는 경우 번역 */
function resolveErrorMessage(message: string | null | undefined, errorCode: string | null | undefined): string {
  if (!message && !errorCode) return '진단 중 오류가 발생했습니다.';
  // message가 ALL_CAPS_UNDERSCORE 패턴이면 에러코드로 간주
  if (message && /^[A-Z][A-Z0-9_]+$/.test(message)) {
    return getErrorMessage(message);
  }
  if (message) return message;
  return getErrorMessage(errorCode!);
}

function SeverityBadge({ severity }: { severity: string }) {
  const color = severity === 'error' ? 'bg-red-100 text-red-700 border-red-200' :
                 severity === 'warning' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                 'bg-blue-100 text-blue-700 border-blue-200';
  const label = severity === 'error' ? '오류' :
                severity === 'warning' ? '경고' : '정보';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${color}`}>{label}</span>;
}

// ─── 가이드 텍스트 파싱 유틸 ──────────────────────────────────────────────────

// 리터럴 \n → 실제 줄바꿈 변환 후, 마크다운 서식 제거해 순수 텍스트 반환
function parseGuideText(raw: string): string {
  return raw
    .replace(/\\n/g, '\n')   // 리터럴 \n → 실제 개행
    .replace(/```[\s\S]*?```/g, (m) => m.replace(/```(?:\w+)?/g, '').trim()) // 코드블록 내 backtick 제거
    .replace(/^#{1,6}\s*/gm, '')  // ### 헤더 제거
    .replace(/\*\*(.+?)\*\*/g, '$1') // **bold** → 텍스트만
    .replace(/`([^`]+)`/g, '$1')    // `inline code` → 텍스트만
    .trim();
}

// 가이드 첫 번째 의미있는 줄(요약) 추출
function extractGuideSummary(raw: string): string {
  const parsed = parseGuideText(raw);
  const lines = parsed.split('\n').map(l => l.trim()).filter(Boolean);
  // 섹션 제목줄(발견된 문제, 수정 방법 등)을 건너뛰고 첫 번째 실제 내용 반환
  const firstContent = lines.find(l => !l.startsWith('🔍') && !l.startsWith('🎯') && !l.startsWith('📝') && !l.startsWith('🔍'));
  const summary = firstContent ?? lines[0] ?? '';
  return summary.length > 60 ? summary.slice(0, 60) + '…' : summary;
}

// 가이드 전체를 섹션별로 렌더링하는 컴포넌트
function GuideContent({ raw }: { raw: string }) {
  const text = parseGuideText(raw);
  return (
    <div className="text-sm text-gray-700 space-y-3 whitespace-pre-wrap break-words">
      {text.split(/\n{2,}/).map((block, i) => {
        const trimmed = block.trim();
        if (!trimmed) return null;
        // 코드 블록 감지 (들여쓰기 없이 <로 시작하는 html)
        if (trimmed.startsWith('<') || trimmed.includes('<img ')) {
          return (
            <pre key={i} className="bg-gray-100 rounded p-2 text-xs overflow-x-auto font-mono">
              {trimmed}
            </pre>
          );
        }
        return <p key={i}>{trimmed}</p>;
      })}
    </div>
  );
}

// 테이블용 가이드 셀 — 요약 표시 + 클릭 시 다이얼로그
function GuideCell({ guide, totalCount, otherIssues }: { guide: string | undefined | null; totalCount?: number; otherIssues?: string[] }) {
  const [open, setOpen] = React.useState(false);
  if (!guide) return <span className="text-gray-400">-</span>;

  const isLong = guide.length > 80 || guide.includes('\\n');
  const summary = isLong ? extractGuideSummary(guide) : parseGuideText(guide);
  const hasMultipleIssues = totalCount && totalCount > 1;

  return (
    <>
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-1.5">
          <span className="text-gray-600 leading-snug">{summary}</span>
          {isLong && (
            <button
              onClick={() => setOpen(true)}
              className="shrink-0 text-xs text-primary hover:underline whitespace-nowrap mt-0.5"
            >
              더보기
            </button>
          )}
        </div>
        {hasMultipleIssues && (
          <span className="text-xs text-red-600 font-medium">
            총 {totalCount}건
          </span>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>수정 가이드 상세</DialogTitle>
          </DialogHeader>
          <GuideContent raw={guide} />
          {hasMultipleIssues && otherIssues && otherIssues.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-600 mb-2">기타 이슈:</p>
              <div className="space-y-1">
                {otherIssues.map((issue: string, idx: number) => (
                  <div key={idx} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded font-mono">
                    {issue}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// AI 품질 세부 점수 항목 메타
const AI_DETAIL_ITEMS = [
  { key: 'specificity',      reasonKey: 'specificityReason',    label: '구체성',        color: '#8b5cf6' },
  { key: 'keywordInclusion', reasonKey: 'keywordReason',        label: '키워드 포함',   color: '#3b82f6' },
  { key: 'wcagCompliance',   reasonKey: 'wcagReason',           label: 'WCAG 준수',     color: '#10b981' },
  { key: 'lengthAppropriate',reasonKey: 'lengthReason',         label: '길이 적절성',   color: '#f59e0b' },
  { key: 'contextRelevance', reasonKey: 'contextReason',        label: '문맥 관련성',   color: '#ef4444' },
] as const;

// 이미지 결과 카드
function ImageResultCard({ img }: { img: DiagnoseImageResponse }) {
  const [expanded, setExpanded] = React.useState(false);
  const hasDetails = img.aiQualityDetails != null;

  const qualityBorderColor = img.aiQualityScore == null ? undefined
    : img.aiQualityScore >= 90 ? 'border-green-300'
    : img.aiQualityScore >= 70 ? 'border-orange-300'
    : img.aiQualityScore >= 50 ? 'border-yellow-300'
    : 'border-red-300';

  return (
    <Card className={cn('overflow-hidden', qualityBorderColor)}>
      {/* 기본 행 */}
      <div className="flex flex-row items-start gap-4 p-3">
        <img
          src={img.src}
          alt={img.altText ?? ''}
          className="w-16 h-16 object-cover rounded border shrink-0"
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-800 truncate text-sm">{img.fileName ?? '이미지'}</div>
          <div className="text-xs text-gray-400 truncate">{img.src}</div>
          {/* ALT 텍스트 미리보기 */}
          {img.altText != null && img.altText.trim() !== '' && (
            <div className="text-xs text-gray-600 mt-1 italic line-clamp-1">"{img.altText}"</div>
          )}
          {img.altText != null && img.altText.trim() === '' && (
            <div className="text-xs text-orange-500 mt-1">
              ⚠ ALT 속성은 존재하지만 값이 비어 있습니다 (빈 문자열). 스크린 리더가 이미지를 설명할 수 없어 접근성 위반입니다.
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {img.altText === null
              ? <Badge className="bg-red-100 text-red-700 border-red-200">ALT 없음</Badge>
              : img.altText.trim() === ''
                ? <Badge className="bg-orange-100 text-orange-700 border-orange-200">ALT 비어있음</Badge>
                : <Badge className="bg-green-100 text-green-700 border-green-200">ALT 있음</Badge>}
            {img.isDecorative && <Badge className="bg-gray-100 text-gray-500 border-gray-200">장식용</Badge>}
            {img.isInLink && <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">링크 내부</Badge>}
            {(img.a11yIssues?.length ?? 0) > 0 && (
              <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                접근성 {img.a11yIssues.length}건
              </Badge>
            )}
            {(img.seoIssues?.length ?? 0) > 0 && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                SEO {img.seoIssues.length}건
              </Badge>
            )}
          </div>
        </div>
        {/* AI 품질 점수 요약 */}
        {img.aiQualityScore != null && (
          <div className="flex flex-col items-center shrink-0 gap-1">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm text-white"
              style={{ backgroundColor: scoreColor(img.aiQualityScore) }}
            >
              {img.aiQualityScore}
            </div>
            <span className="text-xs text-gray-500">AI 품질</span>
            <span className="text-xs font-semibold" style={{ color: scoreColor(img.aiQualityScore) }}>
              {scoreLabel(img.aiQualityScore)}
            </span>
            {hasDetails && (
              <button
                onClick={() => setExpanded(v => !v)}
                className="text-xs text-purple-600 hover:underline mt-0.5 flex items-center gap-0.5"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? '접기' : '상세'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 세부 점수 확장 패널 */}
      {expanded && hasDetails && (
        <div className="border-t bg-purple-50/50 px-4 py-3 space-y-3">
          <p className="text-xs font-semibold text-purple-800 mb-2">AI 품질 세부 평가</p>
          {AI_DETAIL_ITEMS.map(({ key, reasonKey, label, color }) => {
            const score = (img.aiQualityDetails as any)[key] as number;
            const reason = (img.aiQualityDetails as any)[reasonKey] as string | null;
            const pct = Math.max(0, Math.min(100, score));
            return (
              <div key={key}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-700 font-medium">{label}</span>
                  <span className="text-xs font-bold" style={{ color }}>{score}점</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
                {reason && <p className="text-xs text-gray-500 mt-0.5">{reason}</p>}
              </div>
            );
          })}
          {img.aiQualityReason && (
            <p className="text-xs text-purple-700 bg-purple-100 rounded px-2 py-1.5 mt-1">
              💡 {img.aiQualityReason}
            </p>
          )}
          {/* 이슈 가이드 */}
          {(img.a11yIssues?.length > 0 || img.seoIssues?.length > 0) && (
            <div className="pt-2 border-t border-purple-200 space-y-1">
              <p className="text-xs font-semibold text-gray-600">이슈 수정 가이드</p>
              {[...(img.a11yIssues ?? []), ...(img.seoIssues ?? [])].map((issue, idx) => (
                <div key={idx} className="flex items-start gap-1.5 text-xs">
                  <SeverityBadge severity={issue.severity} />
                  <span className="text-gray-700">{issue.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// 접근성 결과 항목 (다중 이슈 지원)
function A11yResultItem({ item }: { item: any }) {
  const hasMultipleIssues = item.totalCount && item.totalCount > 1;
  const otherIssues = item.otherIssues || [];

  return (
    <div className="border rounded-lg p-4 bg-white">
      <div className="flex items-start gap-3 mb-3">
        <SeverityBadge severity={item.severity} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-semibold text-gray-800">{item.message}</p>
            {hasMultipleIssues && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                {item.totalCount}건
              </span>
            )}
          </div>
          {item.scoreImpact !== 0 && (
            <span className="text-xs text-red-500 font-medium">{item.scoreImpact}점 감점</span>
          )}
        </div>
      </div>

      {/* 첫 번째 이슈 상세 가이드 */}
      {item.guide && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
          <p className="text-xs font-semibold text-gray-600 mb-2">상세 수정 가이드:</p>
          <GuideContent raw={item.guide} />
        </div>
      )}

      {/* 나머지 이슈 목록 */}
      {hasMultipleIssues && otherIssues.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-gray-600 mb-2">기타 이슈:</p>
          <div className="space-y-1">
            {otherIssues.map((issue: string, idx: number) => (
              <div key={idx} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded font-mono">
                {issue}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// axe-core 접근성 자동 진단 (개발자 모드에서만)
// 반드시 함수 컴포넌트 내부에서 useEffect로 실행

export default function DiagnosePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  // ─── Phase & URL state ──────────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('input');
  const [urlInput, setUrlInput] = useState('');
  const [jobId, setJobId] = useState<string | null>(searchParams.get('jobId'));

  // ─── Job & Results state ─────────────────────────────────────────────────────
  const [job, setJob] = useState<DiagnoseJobResponse | null>(null);
  const [images, setImages] = useState<DiagnoseImageResponse[]>([]);
  const [imagesPage, setImagesPage] = useState(0);
  const [imagesTotalPages, setImagesTotalPages] = useState(0);
  const [imagesTotalElements, setImagesTotalElements] = useState(0);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [a11y, setA11y] = useState<DiagnoseA11yResponse | null>(null);
  const [a11yLoading, setA11yLoading] = useState(false);
  const [seo, setSeo] = useState<DiagnoseSeoResponse | null>(null);
  const [seoLoading, setSeoLoading] = useState(false);

  // ─── UI state ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('images');
  const [imageFilter, setImageFilter] = useState<'ALL' | 'MISSING_ALT' | 'HAS_ISSUES'>('ALL');
  const [a11yCategory, setA11yCategory] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [serverProgress, setServerProgress] = useState<number | null>(null); // B/E 실제 진행률 (0~100)
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null); // 수정 방법 토글

  // ─── Claim/Fix state ─────────────────────────────────────────────────────────
  const [claiming, setClaiming] = useState(false);
  const [claimDone, setClaimDone] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [fixing, setFixing] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  // 비회원 전환 모달
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [freePlanLimit, setFreePlanLimit] = useState<number>(100); // 무료 플랜 기본값

  // ─── Refs ────────────────────────────────────────────────────────────────────
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resultRef = useRef<HTMLDivElement>(null); // PDF 캡처 대상

  useEffect(() => {
    // CRA 환경: window.location.hostname로 개발환경 감지
    if (window.location.hostname === 'localhost') {
      import('@axe-core/react').then(axe => {
        // CRA 환경에서는 window.ReactDOM이 없을 수 있으나, axe-core는 개발 편의용이므로 예외 허용
        if ((window as any).ReactDOM) {
          axe.default(React, (window as any).ReactDOM, 1000, {});
        }
      }).catch(() => {});
    }
  }, []);
  // ─── 점수 상세 근거 상태 및 로더 ──────────────────────────────
  const [scoreDetails, setScoreDetails] = useState<DiagnoseScoreDetailsResponse | null>(null);
  const loadScoreDetails = useCallback(async (jid: string) => {
    try {
      const details = await diagnoseApi.getScoreDetails(jid);
      setScoreDetails(details);
    } catch {
      // 점수 상세 로드 실패 시 무시
    }
  }, []);

  // ─── 무료 플랜 한도 로드 (모달 표시용) ───────────────────────
  useEffect(() => {
    planApi.getFreePlan().then(res => {
      if (res.data?.monthlyLimit) setFreePlanLimit(res.data.monthlyLimit);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !jobId || claimDone) return;
    const pending = sessionStorage.getItem(PENDING_CLAIM_KEY);
    if (!pending) return;
    try {
      const { jobId: pendingJobId, guestToken } = JSON.parse(pending);
      if (pendingJobId !== jobId) return;
      if (job?.status !== 'DONE') return;
      // Auto-claim
      sessionStorage.removeItem(PENDING_CLAIM_KEY);
      handleClaim(guestToken);
    } catch {
      sessionStorage.removeItem(PENDING_CLAIM_KEY);
    }
  }, [isAuthenticated, jobId, job?.status, claimDone]);

  // ─── Polling ────────────────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback((jid: string) => {
    stopPolling();

    // Poll
    const poll = async () => {
      try {
        // B/E 실제 진행률 조회 (실패해도 무시)
        diagnoseApi.getStatus(jid).then((statusData: DiagnoseStatusResponse) => {
          const pct = (statusData.progress as any)?.percent ?? statusData.progress;
          if (pct != null) {
            setServerProgress(pct as number);
          }
        }).catch(() => {});

        const jobData = await diagnoseApi.getJob(jid, urlInput || undefined);

        // ── errorCode 처리 ──────────────────────────────────────────────────
        if (jobData.errorCode === 'JOB_NOT_FOUND') {
          stopPolling();
          setPhase('failed');
          setJob(jobData);
          return;
        }
        if (jobData.errorCode === 'JOB_AUTO_CREATED') {
          // 새 jobId로 전환하여 폴링 재시작
          const newJobId = jobData.jobId;
          if (!newJobId) return; // 유효하지 않은 jobId 무시
          setJobId(newJobId);
          setJob(jobData);
          setSearchParams({ jobId: newJobId }, { replace: true });
          startPolling(newJobId);
          return;
        }
        // ── 정상 상태 처리 ─────────────────────────────────────────────────
        // 점수 필드가 중첩 객체(scores.accessibility)로 올 경우 정규화
        const raw = jobData as any;
        const normalized = {
          ...jobData,
          scoreAccessibility: jobData.scoreAccessibility
            ?? raw?.scores?.accessibility
            ?? null,
          scoreSeo: jobData.scoreSeo
            ?? raw?.scores?.seo
            ?? null,
        };
        setJob(normalized);
        if (normalized.status === 'DONE') {
          stopPolling();
          setPhase('done');
          setServerProgress(null);
          localStorage.removeItem(CURRENT_DIAGNOSIS_KEY);
          loadResultData(jid);
          recordFunnelEvent('DIAGNOSE', 'RESULT_SHOWN', sessionStorage.getItem(GT_KEY(jid)));
        } else if (normalized.status === 'FAILED') {
          stopPolling();
          setPhase('failed');
          setServerProgress(null);
          localStorage.removeItem(CURRENT_DIAGNOSIS_KEY);
        }
      } catch {
        // keep polling
      }
    };

    poll(); // immediate first call
    pollRef.current = setInterval(poll, 3000);
  }, [stopPolling]);

  // ─── Initial load: jobId present in URL on mount ────────────────────────────
  useEffect(() => {
    // URL에 jobId가 없고, localStorage에 진행 중인 진단이 있으면 복구
    const stored = localStorage.getItem(CURRENT_DIAGNOSIS_KEY);
    const initialJobId = searchParams.get('jobId') ?? (stored ? JSON.parse(stored).jobId : null);
    if (!initialJobId) return;

    // 먼저 로딩 상태 설정
    setPhase('loading');
    setJobId(initialJobId);

    (async () => {
      try {
        const jobData = await diagnoseApi.getJob(initialJobId);
        const raw = jobData as any;
        const normalized = {
          ...jobData,
          scoreAccessibility: jobData.scoreAccessibility ?? raw?.scores?.accessibility ?? null,
          scoreSeo: jobData.scoreSeo ?? raw?.scores?.seo ?? null,
        };
        setJob(normalized);
        if (normalized.status === 'DONE') {
          setPhase('done');
          loadResultData(initialJobId);
        } else if (normalized.status === 'FAILED' || jobData.errorCode === 'JOB_NOT_FOUND') {
          setPhase('failed');
        }
        // still running 상태는 이미 loading으로 설정되어 있으므로 유지
      } catch {
        // fallback: start polling and let it handle
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === 'loading' && jobId) {
      startPolling(jobId);
    }
    return stopPolling;
  }, [phase, jobId]);

  // ─── Elapsed timer: phase가 'loading'이 되는 즉시 시작 (jobId 무관) ──────────
  useEffect(() => {
    if (phase !== 'loading') return;
    setElapsed(0);
    setLoadingStep(0);
    const timer = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 1;
        setLoadingStep(Math.min(Math.floor(next / 10), LOADING_STEPS.length - 1));
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [phase]);

  // ─── Load result data (images tab default) ──────────────────────────────────
  const loadResultData = useCallback(async (jid: string) => {
    loadImagesPage(jid, 0);
    loadScoreDetails(jid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadScoreDetails]);

  const loadImagesPage = useCallback(async (jid: string, page: number, filter?: string) => {
    setImagesLoading(true);
    try {
      const raw = await diagnoseApi.getImages(jid, page, 20, filter);
      // B/E 응답이 { content: [] } 또는 { items: [] } 둘 다 지원
      const data = raw as any;
      const content: DiagnoseImageResponse[] = data.content ?? data.items ?? [];
      setImages(page === 0 ? content : prev => [...(prev ?? []), ...content]);
      setImagesPage(page);
      setImagesTotalPages(data.totalPages ?? 0);
      setImagesTotalElements(data.totalElements ?? data.total ?? content.length);
    } catch { /* ignore */ }
    setImagesLoading(false);
  }, []);

  const loadA11y = useCallback(async (jid: string) => {
    if (a11y) return;
    setA11yLoading(true);
    try {
      const data = await diagnoseApi.getA11y(jid);
      const raw = data as any;
      // B/E 응답은 items 필드로 내려온다
      const resultItems = Array.isArray(raw?.items) ? raw.items
        : Array.isArray(raw?.results) ? raw.results : [];
      const byCat: CategorySummary[] = Array.isArray(raw?.byCategory) ? raw.byCategory
        : (raw?.byCategory && typeof raw.byCategory === 'object') ? Object.values(raw.byCategory) as CategorySummary[] : [];
      setA11y({
        ...data,
        items: resultItems,
        results: resultItems,
        byCategory: byCat,
        manualCheckItems: Array.isArray(data?.manualCheckItems) ? data.manualCheckItems : [],
        kwcagCoverage: data?.kwcagCoverage ?? null,
      });
    } catch { /* ignore */ }
    setA11yLoading(false);
  }, [a11y]);

  const loadSeo = useCallback(async (jid: string) => {
    if (seo) return;
    setSeoLoading(true);
    try {
      const data = await diagnoseApi.getSeo(jid);
      const raw = data as any;
      setSeo({
        ...data,
        items: Array.isArray(data?.items) ? data.items : [],
        // FE내부에서 score/detail 필드를 참조하는 코드를 위해 폴백
        score: raw?.psiScore ?? 0,
        detail: raw?.detail ?? null,
      });
    } catch { /* ignore */ }
    setSeoLoading(false);
  }, [seo]);

  // ─── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;
    if (!isAuthenticated && TURNSTILE_ENABLED && !turnstileToken) return;

    // URL 정규화
    let url = urlInput.trim();
    if (!/^https?:\/\//i.test(url)) url = 'https://' + url;

    setSubmitError(null);
    setPhase('loading');
    setJob(null);
    setImages([]);
    setA11y(null);
    setSeo(null);
    setActiveTab('images');

    try {
      const res = await diagnoseApi.start(url, undefined, turnstileToken || undefined);

      // 에러코드 체크 (백엔드가 HTTP 200으로 errorCode를 바디에 담아 내려줄 때)
      if (res.errorCode) {
        setPhase('input');
        setSubmitError(res.message || getErrorMessage(res.errorCode));
        return;
      }

      setJobId(res.jobId);
      setSearchParams({ jobId: res.jobId });
      // 진단 시작 기록 (새로고침/로그아웃 후 복구용)
      localStorage.setItem(CURRENT_DIAGNOSIS_KEY, JSON.stringify({
        jobId: res.jobId,
        targetUrl: url,
        startTime: new Date().toISOString(),
      }));
      // guestToken 저장
      if (res.guestToken) {
        sessionStorage.setItem(GT_KEY(res.jobId), res.guestToken);
      }
      recordFunnelEvent('DIAGNOSE', 'START', res.guestToken);
    } catch (err: any) {
      setPhase('input');
      // HTTP 에러 응답의 errorCode 체크
      const errorCode = err.response?.data?.errorCode;
      const msg = errorCode
        ? (err.response?.data?.message || getErrorMessage(errorCode))
        : (err.response?.data?.message || err.response?.data?.error || '진단 요청에 실패했습니다.');
      setSubmitError(msg);
    }
  };

  // ─── Re-diagnose ────────────────────────────────────────────────────────────
  const handleReDiagnose = async () => {
    if (!job) return;
    const parentId = job.version === 1 ? job.jobId : (job.parentJobId ?? job.jobId);

    setSubmitError(null);
    setPhase('loading');
    setJob(null);
    setImages([]);
    setA11y(null);
    setSeo(null);
    setActiveTab('images');

    try {
      const res = await diagnoseApi.start(job.targetUrl, parentId);

      // 에러코드 체크
      if (res.errorCode) {
        setPhase('done');
        setSubmitError(res.message || getErrorMessage(res.errorCode));
        return;
      }

      setJobId(res.jobId);
      setSearchParams({ jobId: res.jobId });
      if (res.guestToken) {
        sessionStorage.setItem(GT_KEY(res.jobId), res.guestToken);
      }
    } catch (err: any) {
      setPhase('done');
      const errorCode = err.response?.data?.errorCode;
      const msg = errorCode
        ? (err.response?.data?.message || getErrorMessage(errorCode))
        : (err.response?.data?.message || '재진단 요청에 실패했습니다.');
      setSubmitError(msg);
    }
  };

  // ─── PDF Download ─────────────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    window.print();
  };

  // ─── Claim ──────────────────────────────────────────────────────────────────
  const handleClaim = async (overrideToken?: string) => {
    if (!jobId) return;
    const guestToken = overrideToken ?? sessionStorage.getItem(GT_KEY(jobId));
    if (!guestToken) return;

    setClaiming(true);
    setClaimError(null);
    try {
      const res = await diagnoseApi.claim(jobId, guestToken);
      sessionStorage.removeItem(GT_KEY(jobId));
      setClaimDone(true);
      if (res.redirectUrl) {
        navigate(res.redirectUrl);
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || '결과 저장에 실패했습니다.';
      setClaimError(msg);
    }
    setClaiming(false);
  };

  // ─── Save (login redirect for guests) ───────────────────────────────────────
  const handleLoginAndClaim = () => {
    if (!jobId) return;
    const guestToken = sessionStorage.getItem(GT_KEY(jobId));
    if (guestToken) {
      sessionStorage.setItem(PENDING_CLAIM_KEY, JSON.stringify({ jobId, guestToken }));
    }
    sessionStorage.setItem('redirectAfterLogin', `/diagnose?jobId=${jobId}`);
    navigate('/login');
  };

  // ─── Fix ─────────────────────────────────────────────────────────────────────
  const handleFix = async (overrideGuestToken?: string) => {
    if (!jobId) return;
    setFixing(true);
    setFixError(null);
    setShowGuestModal(false);
    try {
      const token = overrideGuestToken ?? undefined;
      const res = await diagnoseApi.fix(jobId, { imageIds: [] }, token);
      navigate(res.redirectUrl);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'ALT 텍스트 생성 요청에 실패했습니다.';
      setFixError(msg);
      setFixing(false);
    }
  };

  // 비회원: 모달 열기 (로그인 없이 바로 개선 시도)
  const handleGuestFixClick = () => {
    setShowGuestModal(true);
  };

  // 비회원 → 회원가입 후 개선 (signup redirect)
  const handleSignupAndFix = () => {
    let gt: string | null = null;
    if (jobId) {
      gt = sessionStorage.getItem(GT_KEY(jobId));
      if (gt) sessionStorage.setItem(PENDING_CLAIM_KEY, JSON.stringify({ jobId, guestToken: gt }));
      sessionStorage.setItem('redirectAfterLogin', `/diagnose?jobId=${jobId}`);
    }
    recordFunnelEvent('DIAGNOSE', 'SIGNUP_CTA_CLICK', gt);
    navigate('/signup');
  };


  // ─── Computed ────────────────────────────────────────────────────────────────
  const guestToken = jobId ? sessionStorage.getItem(GT_KEY(jobId)) : null;
  const canClaim = isAuthenticated && !!guestToken && !claimDone;
  const canFix = isAuthenticated && job?.status === 'DONE' && (job.summary?.fixableByAlttext ?? 0) > 0;

  // 백엔드에서 필터링하므로 클라이언트 사이드 필터링 제거
  const filteredImages = images ?? [];

  const a11yCategories = a11y
    ? Array.from(new Set((Array.isArray(a11y.results) ? a11y.results : []).map(r => r.category)))
    : [];

  const filteredA11y = (Array.isArray(a11y?.results) ? a11y!.results : []).filter(r =>
    a11yCategory === null || r.category === a11yCategory
  );

  // 탭 변경 시 접근성/SEO 데이터 비동기 로딩
  useEffect(() => {
    if (phase !== 'done' || !jobId) return;
    // 접근성 탭은 항상 미리 로드 (9개 항목 표 때문에)
    loadA11y(jobId);
    if (activeTab === 'seo') loadSeo(jobId);
  }, [activeTab, phase, jobId]);

  // ─── Render: Input ───────────────────────────────────────────────────────────
  const renderInput = () => (
    <div className="flex-1">
      {/* Hero */}
      <section className="bg-gradient-to-b from-primary/5 to-white py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            무료 접근성 & SEO 진단
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            웹 접근성 & SEO 이미지<br />무료 진단
          </h1>
          <p className="text-lg text-gray-500 mb-8">
            KWCAG 2.2 기준 핵심 9개 항목 자동 진단 · AI 이미지 품질 평가 · SEO 분석
          </p>

          {/* URL 입력 폼 */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="url"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                placeholder="https://www.yoursite.com"
                className="pl-10 h-12 text-base"
                required
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="h-12 px-8 gap-2"
              disabled={!isAuthenticated && TURNSTILE_ENABLED && !turnstileToken}
            >
              <Search className="w-5 h-5" />
              진단 시작
            </Button>
          </form>

          {!isAuthenticated && (
            <div className="mt-3 flex justify-center">
              <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />
            </div>
          )}

          {submitError && (
            <Alert variant="destructive" className="mt-4 text-left">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="ml-2">{submitError}</AlertDescription>
            </Alert>
          )}

          <p className="text-xs text-gray-400 mt-4">하루 3회 무료 진단 · 실패한 진단은 횟수에 포함되지 않음 · 결과 30일 보관 (로그인 시)</p>
        </div>
      </section>

      {/* 진단 항목 상세 소개 */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">무엇을 진단하나요?</h2>
            <p className="text-gray-500 text-base">URL 하나로 4가지 영역을 자동으로 분석합니다</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">

            {/* 1. KWCAG 2.2 접근성 9개 항목 */}
            <Card className="border-blue-100 shadow-sm overflow-hidden">
              <CardHeader className="pb-3 bg-blue-50/60 border-b border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base text-blue-900">KWCAG 2.2 접근성 핵심 9개 항목 자동 검사</CardTitle>
                    <p className="text-xs text-blue-600 mt-0.5">한국형 웹 콘텐츠 접근성 지침 2.2 기반</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'IMG_MISSING_ALT',   name: '이미지 대체 텍스트', auto: true },
                    { id: 'LINK_PURPOSE',       name: '링크 텍스트 명확성', auto: true },
                    { id: 'COLOR_CONTRAST',     name: '텍스트 명도 대비',   auto: true },
                    { id: 'KEYBOARD_ACCESS',    name: '키보드 접근성',       auto: true },
                    { id: 'FOCUS_VISIBLE',      name: '초점 표시',           auto: true },
                    { id: 'PAGE_TITLE',         name: '페이지 제목 제공',   auto: true },
                    { id: 'HEADING_STRUCTURE',  name: '제목 계층 구조',     auto: true },
                    { id: 'FORM_LABEL',         name: '폼 레이블 제공',     auto: true },
                    { id: 'LANG_ATTRIBUTE',     name: '언어 속성 설정',     auto: true },
                  ].map(({ id, name, auto }) => (
                    <div key={id} className="flex items-start gap-1.5 text-xs">
                      <span className={cn(
                        'shrink-0 mt-0.5 w-2 h-2 rounded-full',
                        auto ? 'bg-green-400' : 'bg-yellow-400'
                      )} />
                      <span className="text-gray-400 font-mono text-[10px]">{id}</span>
                      <span className="text-gray-600 leading-tight">{name}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" />자동 탐지</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" />수동 확인 안내</span>
                  <span className="ml-auto text-blue-600 font-medium">핵심 9개 항목</span>
                </div>
              </CardContent>
            </Card>

            {/* 2. Google PSI 기반 SEO 분석 */}
            <Card className="border-green-100 shadow-sm overflow-hidden">
              <CardHeader className="pb-3 bg-green-50/60 border-b border-green-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base text-green-900">Google PSI 기반 SEO 이미지 최적화 분석</CardTitle>
                    <p className="text-xs text-green-600 mt-0.5">PageSpeed Insights · Lighthouse 연동</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-2.5">
                {[
                  { icon: '🖼️', label: 'ALT 완성도', desc: '전체 이미지 중 ALT 텍스트가 있는 비율 (0~100%)' },
                  { icon: '📊', label: 'Google PSI 점수', desc: 'PageSpeed Insights API 직접 연동 실시간 측정' },
                  { icon: '📝', label: '메타 설명', desc: '검색 결과에 표시되는 meta description 설정 여부 확인' },
                  { icon: '🔗', label: 'OG 이미지', desc: 'Open Graph 이미지 설정 여부 — SNS 공유 품질 직결' },
                  { icon: '🗂️', label: '구조화 데이터 (JSON-LD)', desc: 'Schema.org 마크업 존재 여부 확인' },
                  { icon: '⚡', label: 'WebP 이미지 최적화', desc: '차세대 포맷 사용 비율 — 페이지 속도 영향' },
                  { icon: '🦥', label: 'Lazy Loading 적용률', desc: 'loading="lazy" 이미지 비율' },
                ].map(({ icon, label, desc }) => (
                  <div key={label} className="flex items-start gap-2.5">
                    <span className="text-base shrink-0 mt-0.5">{icon}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{label}</p>
                      <p className="text-xs text-gray-500">{desc}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* 3. ALT 품질 평가 */}
            <Card className="border-purple-100 shadow-sm overflow-hidden">
              <CardHeader className="pb-3 bg-purple-50/60 border-b border-purple-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base text-purple-900">ALT 품질 평가 (0~100점)</CardTitle>
                    <p className="text-xs text-purple-600 mt-0.5">이미지별 ALT 텍스트 품질 평가</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div className="space-y-2">
                  {[
                    { range: '90~100', color: 'bg-green-500', label: '매우 우수', desc: '정확하고 구체적인 대체 텍스트, WCAG 2.1 완전 충족' },
                    { range: '70~89',  color: 'bg-green-400', label: '양호',     desc: '맥락에 맞는 설명, 개선 여지 소폭 존재' },
                    { range: '50~69',  color: 'bg-yellow-400', label: '보통',    desc: '기본적 설명 존재하나 구체성·키워드 부족' },
                    { range: '0~49',   color: 'bg-red-400',   label: '미흡',     desc: '너무 짧거나 의미없는 텍스트, 즉시 개선 필요' },
                  ].map(({ range, color, label, desc }) => (
                    <div key={range} className="flex items-start gap-2.5">
                      <div className={cn('w-1.5 rounded-full shrink-0 mt-1', color)} style={{ height: '2.5rem' }} />
                      <div>
                        <p className="text-xs text-gray-500 font-mono">{range}점</p>
                        <p className="text-sm font-medium text-gray-800">{label}</p>
                        <p className="text-xs text-gray-500">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t border-gray-100 text-xs text-gray-500 bg-purple-50 rounded-lg px-3 py-2">
                  💡 ALT 텍스트가 없는 이미지는 AI가 직접 생성 후 품질을 채점합니다
                </div>
              </CardContent>
            </Card>

            {/* 4. 접근성·SEO 점수 + 이슈별 수정 가이드 */}
            <Card className="border-orange-100 shadow-sm overflow-hidden">
              <CardHeader className="pb-3 bg-orange-50/60 border-b border-orange-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                    <ClipboardCheck className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base text-orange-900">접근성·SEO 점수 + 이슈별 수정 가이드</CardTitle>
                    <p className="text-xs text-orange-600 mt-0.5">업계 평균 기준 감점 방식 — 실제 이슈별 가이드 제공</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                {/* 점수 계산 방식 */}
                <div className="rounded-lg bg-gray-50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-600">점수 산출 방식</p>
                  <p className="text-xs text-gray-500">업계 평균 기준 감점 방식 — 실제 이슈별 가이드 제공</p>
                  <div className="grid grid-cols-2 gap-1.5 mt-1">
                    <div className="bg-blue-50 rounded-md px-2 py-1.5">
                      <p className="text-[10px] text-blue-500 font-medium">접근성 점수</p>
                      <p className="text-sm font-bold text-blue-700">0 ~ 120점</p>
                      <p className="text-[10px] text-blue-400">100점 초과 가능</p>
                    </div>
                    <div className="bg-green-50 rounded-md px-2 py-1.5">
                      <p className="text-[10px] text-green-500 font-medium">SEO 점수</p>
                      <p className="text-sm font-bold text-green-700">0 ~ 100점</p>
                      <p className="text-[10px] text-green-400">실제 이슈 기반 감점</p>
                    </div>
                  </div>
                </div>
                {/* 수정 가이드 예시 */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-600">이슈별 수정 가이드 예시</p>
                  {[
                    { badge: '오류', badgeColor: 'bg-red-100 text-red-700', issue: 'ALT 없는 이미지', guide: '<img alt="상품명과 특징을 3~10단어로 기술">  감점: 1개당 -3점 (업계 평균 기준)' },
                    { badge: '경고', badgeColor: 'bg-yellow-100 text-yellow-700', issue: 'H1 태그 없음', guide: '페이지당 H1 태그 1개, 핵심 키워드 포함' },
                    { badge: '정보', badgeColor: 'bg-blue-100 text-blue-700', issue: 'OG 이미지 미설정', guide: '<meta property="og:image" content="…"> 추가' },
                  ].map(({ badge, badgeColor, issue, guide }) => (
                    <div key={issue} className="border border-gray-100 rounded-lg p-2.5 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', badgeColor)}>{badge}</span>
                        <span className="text-xs font-medium text-gray-700">{issue}</span>
                      </div>
                      <p className="text-xs text-gray-500 font-mono pl-1 truncate">{guide}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>
    </div>
  );

  // ─── Render: Loading ─────────────────────────────────────────────────────────
  const renderLoading = () => {
    // B/E 실제 진행률 우선, 없으면 경과 시간 기반 추정 (최대 99%)
    const progress = serverProgress != null
      ? Math.min(serverProgress, 99)
      : Math.min(Math.round((elapsed / 90) * 100), 99);

    return (
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="max-w-lg w-full">

          {/* 헤더 카드 */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-4">
            {/* 스피너 + 아이콘 */}
            <div className="flex items-center justify-center mb-6">
              <div className="relative w-24 h-24">
                {/* 바깥 회전 링 */}
                <svg className="absolute inset-0 w-full h-full animate-spin" viewBox="0 0 96 96" fill="none">
                  <circle cx="48" cy="48" r="44" stroke="#e2e8f0" strokeWidth="4" />
                  <circle cx="48" cy="48" r="44"
                    stroke="url(#grad)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray="138 138"
                    strokeDashoffset="104"
                  />
                  <defs>
                    <linearGradient id="grad" x1="0" y1="0" x2="96" y2="96" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#6366f1" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                  </defs>
                </svg>
                {/* 중앙 아이콘 */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Shield className="w-9 h-9 text-primary" />
                </div>
              </div>
            </div>

            {/* 제목 */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                사이트 진단 중...
              </h2>
              <p className="text-sm text-gray-500 mb-3">접근성 · SEO · AI 품질을 종합 분석하고 있습니다</p>
              {job?.targetUrl && (
                <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5 max-w-full">
                  <Globe className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="text-xs text-gray-600 font-mono truncate max-w-xs">{job.targetUrl.replace(/^https?:\/\//, '')}</span>
                </div>
              )}
            </div>

            {/* 진행률 바 */}
            <div className="mb-6">
              <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                <span>분석 진행률</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000 ease-linear"
                  style={{
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #6366f1, #3b82f6)',
                  }}
                />
              </div>
            </div>

            {/* 스텝 목록 */}
            <div className="space-y-1.5">
              {LOADING_STEPS.map(({ label, icon: Icon }, idx) => {
                const done = idx < loadingStep;
                const active = idx === loadingStep;
                return (
                  <div
                    key={idx}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300',
                      done   ? 'opacity-40' :
                      active ? 'bg-primary/8 border border-primary/20 shadow-sm' :
                               'opacity-25'
                    )}
                  >
                    <div className={cn(
                      'w-7 h-7 rounded-full flex items-center justify-center shrink-0',
                      done   ? 'bg-green-100' :
                      active ? 'bg-primary/15' :
                               'bg-gray-100'
                    )}>
                      {done
                        ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                        : active
                          ? <Spinner size="sm" className="text-primary" />
                          : <Icon className="w-4 h-4 text-gray-400" />
                      }
                    </div>
                    <span className={cn(
                      'text-sm',
                      active ? 'font-semibold text-primary' : 'text-gray-600'
                    )}>
                      {label}
                    </span>
                    {done && (
                      <span className="ml-auto text-xs text-green-500 font-medium">완료</span>
                    )}
                    {active && (
                      <span className="ml-auto flex gap-0.5">
                        {[0,1,2].map(i => (
                          <span
                            key={i}
                            className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce"
                            style={{ animationDelay: `${i * 0.15}s` }}
                          />
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 하단 타이머 바 */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-3 flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center gap-1.5">
              <Timer className="w-3.5 h-3.5" />
              <span>경과 시간</span>
            </div>
            <span className="font-mono font-semibold text-gray-600 tabular-nums">{elapsed}초</span>
            <span className="text-gray-300">|</span>
            <span>최대 {job?.estimatedSeconds ?? 90}초 소요</span>
            <div className="w-20 h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-400 to-blue-400 rounded-full transition-all duration-1000"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

        </div>
      </div>
    );
  };

  // ─── Render: Result ──────────────────────────────────────────────────────────
  const renderResult = () => {

    // 방어 코드: undefined.length 에러 방지
    const safeScoreDetails = scoreDetails || { seoScoreDetails: [], a11yScoreDetails: [] };
    const safeA11y = a11y || { results: [], manualCheckItems: [], byCategory: [], kwcagCoverage: null };
    if (!job) return null;
    const summary = job.summary as DiagnoseSummaryPatched;

    // 점수 클램프: B/E 버그 방어 (0~100 범위 강제)
    const clampScore = (s: number | null | undefined): number | null =>
      s == null ? null : Math.max(0, Math.min(100, s));
    const safeScoreA11y = clampScore(job.scoreAccessibility);
    const safeScoreSeo  = clampScore(job.scoreSeo);

    // 실제 적용 감점: 새 필드(totalA11yDeduction) 우선, 없으면 score-details의 deductionSummary, 최후 폴백은 100-score
    const a11yActualDeduction: number =
      job.totalA11yDeduction
      ?? safeScoreDetails.deductionSummary?.appliedA11yDeduction
      ?? Math.max(0, 100 - (safeScoreA11y ?? 100));
    const seoActualDeduction: number =
      job.totalSeoDeduction
      ?? safeScoreDetails.deductionSummary?.appliedSeoDeduction
      ?? Math.max(0, 100 - (safeScoreSeo ?? 100));

    // 이미지 탭: 전체 이미지 수 표시 (totalImages 우선, 없으면 로드된 수)
    const totalImageCount = summary?.totalImages ?? imagesTotalElements ?? images.length;

    // 상위 이슈 목록: a11y 실패 + seo 감점 항목, 감점 내림차순 top 5
    const topIssues: { severity: string; text: string; impact: string; category: '접근성' | 'SEO'; guide?: string }[] = [
      ...(safeScoreDetails.a11yScoreDetails as DiagnoseA11yScoreDetail[])
        .filter(d => d.result === 'FAIL' && d.deduction > 0)
        .map(d => ({
          severity: d.deduction >= 10 ? 'error' : d.deduction >= 3 ? 'warning' : 'info',
          text: d.name,
          impact: `-${d.deduction}점`,
          category: '접근성' as const,
          guide: d.guide || d.reason,
          _score: d.deduction,
        })),
      ...(safeScoreDetails.seoScoreDetails as DiagnoseSeoScoreDetail[])
        .filter(d => d.deduction > 0)
        .map(d => ({
          severity: d.deduction >= 10 ? 'error' : d.deduction >= 3 ? 'warning' : 'info',
          text: d.message,
          impact: `-${d.deduction}점`,
          category: 'SEO' as const,
          guide: d.guide,
          _score: d.deduction,
        })),
    ]
      .sort((a, b) => (b as any)._score - (a as any)._score)
      .slice(0, 5)
      .map(({ severity, text, impact, category, guide }) => ({ severity, text, impact, category, guide }));

    return (
      <div className="flex-1 bg-gray-50">
        <div ref={resultRef} className="container mx-auto px-4 max-w-5xl py-8 space-y-6">

          {/* 랜딩페이지와 동일한 결과 요약 카드 */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-6">
            {/* 상단 URL 바 */}
            <div className="bg-gray-50 border-b px-5 py-3 flex items-center gap-3">
              <Globe className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-500 truncate flex-1">{job.targetUrl}</span>
              <Badge className="bg-gray-100 text-gray-600 border-gray-200 shrink-0 text-xs font-normal">v{job.version}</Badge>
              <span className="shrink-0 text-xs bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full">진단 완료</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPDF}
                className="gap-1.5 text-xs h-8"
              >
                <Download className="w-3.5 h-3.5" />
                PDF 다운로드
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReDiagnose}
                className="gap-1.5 text-xs h-8"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                수정 후 재진단
              </Button>
            </div>

            {/* 점수 2-column grid */}
            <div className="grid grid-cols-2 divide-x border-b">
              {[
                { label: '접근성 점수', score: safeScoreA11y },
                { label: 'SEO 점수',    score: safeScoreSeo },
              ].map(({ label, score }) => (
                <div key={label} className="py-5 text-center">
                  <p className="text-3xl font-bold" style={{ color: scoreColor(score) }}>{score ?? '—'}</p>
                  <p className="text-xs text-gray-400 mt-1">{label}</p>
                  <p className="text-xs font-medium mt-1" style={{ color: scoreColor(score) }}>{scoreLabel(score)}</p>
                </div>
              ))}
            </div>

            {/* 발견된 주요 이슈 */}
            <div className="p-5 space-y-2.5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">발견된 주요 이슈</p>
                <p className="text-xs text-gray-400">감점 내림차순 상위 5개 · 상세는 접근성/SEO 탭에서 확인</p>
              </div>
              {topIssues.length > 0 ? topIssues.map(({ severity, text, impact, category }) => (
                <div key={text} className="border-b border-gray-50 last:border-0 py-2">
                  <div className="flex items-center gap-2.5">
                    <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                      severity === 'error'   ? 'bg-red-100 text-red-700' :
                      severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                               'bg-blue-100 text-blue-700'
                    }`}>
                      {severity === 'error' ? '오류' : severity === 'warning' ? '경고' : '정보'}
                    </span>
                    <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                      category === '접근성' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'
                    }`}>
                      {category}
                    </span>
                    <span className="text-xs text-gray-700 flex-1">{text}</span>
                    <span className="text-xs text-red-500 font-medium shrink-0">{impact}</span>
                  </div>
                </div>
              )) : (
                <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  발견된 주요 이슈가 없습니다.
                </div>
              )}
              {summary?.impactMessage && (
                <p className="text-xs text-orange-600 mt-2">{summary.impactMessage}</p>
              )}
            </div>

            {/* ALT 자동생성 CTA */}
            {(summary?.fixableByAlttext ?? 0) > 0 && (
              <div className="bg-primary/5 border-t px-5 py-3 flex items-center justify-between">
                <span className="text-sm text-primary font-medium">ALT 자동생성 가능 이미지 {summary?.fixableByAlttext}개</span>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Zap className="w-3 h-3" /> AI로 즉시 수정
                </span>
              </div>
            )}
          </div>

          {/* 강화된 CTA 섹션 */}
          {!isAuthenticated && (
            <Card className="bg-gradient-to-br from-primary/5 to-blue-50 border-2 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      이미지 ALT 텍스트를 개선하려면?
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      ALT 텍스트가 없거나 개선이 필요한 이미지를 AI가 자동으로 생성해 드립니다.
                      무료 플랜으로 지금 바로 시작하세요.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        onClick={handleSignupAndFix}
                        className="bg-primary text-white font-medium"
                      >
                        무료 회원가입 후 개선 시작
                      </Button>
                      <Button
                        onClick={() => {
                          if (isAuthenticated) {
                            navigate('/inquiry/new');
                          } else {
                            navigate('/login', { state: { from: '/inquiry/new' } });
                          }
                        }}
                        variant="outline"
                        className="gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        전문가에게 문의하기
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {submitError && (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="images">이미지({totalImageCount})</TabsTrigger>
              <TabsTrigger value="a11y">접근성{a11yActualDeduction > 0 ? `(-${a11yActualDeduction}점)` : ''}</TabsTrigger>
              <TabsTrigger value="seo">SEO{seoActualDeduction > 0 ? `(-${seoActualDeduction}점)` : ''}</TabsTrigger>
            </TabsList>

            {/* ── 이미지 탭 ── */}
            <TabsContent value="images" className="mt-4 space-y-4">
              {/* AI 품질 점수 안내 */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-blue-800">ALT 품질 평가 (0~100점)</span>
                </div>
                <p className="text-xs text-blue-700 mb-2">이미지별 ALT 텍스트 품질 평가</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                  {[
                    { range: '90~100점', label: '매우 우수', desc: '정확하고 구체적, WCAG 2.1 완전 충족', color: '#16a34a' },
                    { range: '70~89점',  label: '양호',     desc: '맥락에 맞는 설명, 개선 여지 소폭 존재', color: '#2563eb' },
                    { range: '50~69점',  label: '보통',     desc: '기본 설명 존재하나 구체성·키워드 부족', color: '#d97706' },
                    { range: '0~49점',   label: '미흡',     desc: '너무 짧거나 의미 없는 텍스트, 즉시 개선 필요', color: '#dc2626' },
                  ].map(({ range, label, desc, color }) => (
                    <div key={range} className="bg-white rounded-lg border px-2.5 py-2">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-xs font-bold" style={{ color }}>{label}</span>
                        <span className="text-xs text-gray-400 ml-auto">{range}</span>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-blue-600 mt-2">💡 ALT 텍스트가 없는 이미지는 AI가 직접 생성 후 품질을 채점합니다.</p>
              </div>

              <div className="flex gap-2 flex-wrap">
                {(['ALL', 'MISSING_ALT', 'HAS_ISSUES'] as const).map(f => (
                  <Button
                    key={f}
                    variant={imageFilter === f ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setImageFilter(f);
                      if (jobId) {
                        setImages([]);
                        setImagesPage(0);
                        const filterParam = f === 'ALL' ? undefined : f === 'MISSING_ALT' ? 'no-alt' : 'has-issue';
                        loadImagesPage(jobId, 0, filterParam);
                      }
                    }}
                  >
                    {f === 'ALL' ? '전체' : f === 'MISSING_ALT' ? 'ALT 없음' : '이슈 있음'}
                  </Button>
                ))}
              </div>

              {imagesLoading && images.length === 0 ? (
                <LoadingBlock size="2xl" />
              ) : filteredImages.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>해당 조건의 이미지가 없습니다.</p>
                  {images.length === 0 && imagesTotalPages > 1 && (
                    <p className="text-xs text-gray-400 mt-1">이미지 데이터를 불러오는 중입니다.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredImages.map(img => (
                    <ImageResultCard key={img.id} img={img} />
                  ))}
                </div>
              )}

              {/* 더 보기: 남은 개수 > 0 일 때만 표시 */}
              {imagesPage + 1 < imagesTotalPages && imagesTotalElements > images.length && (
                <div className="text-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const filterParam = imageFilter === 'ALL' ? undefined : imageFilter === 'MISSING_ALT' ? 'no-alt' : 'has-issue';
                      jobId && loadImagesPage(jobId, imagesPage + 1, filterParam);
                    }}
                    disabled={imagesLoading}
                  >
                    {imagesLoading ? <Spinner size="sm" className="mr-2" /> : null}
                    더 보기 ({imagesTotalElements - images.length}개 더)
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* ── 접근성 탭 ── */}
            <TabsContent value="a11y" className="mt-4 space-y-4">
              {a11yLoading ? (
                <LoadingBlock size="2xl" />
              ) : !a11y ? (
                <div className="text-center py-12 text-gray-500">데이터를 불러오는 중입니다.</div>
              ) : (
                <>
                  {/* 접근성 점수 요약 카드 */}
                  <Card className="bg-gray-50 border-gray-200">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex flex-wrap gap-6 items-center">
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">접근성 점수</p>
                          <p className="text-3xl font-bold" style={{color: scoreColor(safeScoreA11y)}}>
                            {safeScoreA11y ?? '—'}점
                          </p>
                          <p className="text-xs font-semibold mt-0.5" style={{color: scoreColor(safeScoreA11y)}}>{scoreLabel(safeScoreA11y)}</p>
                        </div>
                        <div className="text-gray-300 text-2xl">=</div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">기본 점수</p>
                          <p className="text-2xl font-bold text-gray-700">100점</p>
                        </div>
                        {a11yActualDeduction > 0 && (
                          <>
                            <div className="text-gray-300 text-2xl">−</div>
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">감점 합계</p>
                              <p className="text-2xl font-bold text-red-600">{a11yActualDeduction}점</p>
                            </div>
                          </>
                        )}
                        <div className="ml-auto text-right">
                          <p className="text-xs text-gray-500">검사 항목</p>
                          <p className="text-lg font-bold text-gray-700">
                            {safeScoreDetails.a11yScoreDetails.length > 0
                              ? (() => {
                                  const checked = (safeScoreDetails.a11yScoreDetails as DiagnoseA11yScoreDetail[]).filter(d => d.result !== 'NOT_CHECKED');
                                  const passed = checked.filter(d => d.result === 'PASS').length;
                                  return `${passed} / ${checked.length} 통과`;
                                })()
                              : a11y.kwcagCoverage && typeof a11y.kwcagCoverage === 'object'
                                ? `${a11y.kwcagCoverage.autoChecked} / ${a11y.kwcagCoverage.totalKwcag}`
                                : '—'
                            }
                          </p>
                          <p className="text-xs text-gray-400">자동 검사 항목</p>
                        </div>
                      </div>
                      {safeScoreA11y === 0 && a11yActualDeduction >= 100 && (
                        <p className="text-xs text-red-500 mt-2 pt-2 border-t border-red-100">
                          💡 감점 합계({a11yActualDeduction}점)가 기본 점수(100점)를 초과하여 0점으로 처리됩니다.
                        </p>
                      )}
                      {(a11y.byCategory ?? []).length > 0 && (
                        <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-200">
                          {(a11y.byCategory ?? []).map(cat => (
                            <div key={cat.category} className="text-sm">
                              <span className="text-gray-600">{cat.category}: </span>
                              <span className="font-semibold text-red-600">{Math.abs(cat.scoreImpact)}점 감점</span>
                              <span className="text-gray-400 ml-1">({cat.issueCount}건)</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Category filter */}
                  {a11yCategories.length > 1 && (
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant={a11yCategory === null ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setA11yCategory(null)}
                      >
                        전체
                      </Button>
                      {a11yCategories.map(cat => (
                        <Button
                          key={cat}
                          variant={a11yCategory === cat ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setA11yCategory(cat)}
                        >
                          {cat}
                        </Button>
                      ))}
                    </div>
                  )}

                  {/* 접근성 항목 전체 결과 표 */}
                  <Card className="border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-base text-blue-800">KWCAG 2.2 자동 검사 항목 결과</CardTitle>
                      <p className="text-xs text-gray-500 mt-1">
                        웹 접근성 국제 표준(KWCAG 2.2) {safeScoreDetails.a11yScoreDetails.length > 0 ? `${(safeScoreDetails.a11yScoreDetails as DiagnoseA11yScoreDetail[]).filter(d => d.result !== 'NOT_CHECKED').length}개` : ''} 항목을 자동으로 검사한 결과입니다.
                        <strong>FAIL</strong> 항목의 감점이 합산되어 접근성 점수(100점 기준)가 계산됩니다.
                        <strong>수동 확인 필요</strong>는 자동 검사 불가 — 아래 가이드를 참고해 직접 점검해 주세요.
                      </p>
                    </CardHeader>
                    <CardContent className="overflow-x-auto p-0">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 text-gray-700">
                            <th className="px-3 py-2 text-left border-b whitespace-nowrap">분류</th>
                            <th className="px-3 py-2 text-left border-b">항목</th>
                            <th className="px-3 py-2 text-center border-b whitespace-nowrap">결과</th>
                            <th className="px-3 py-2 text-right border-b whitespace-nowrap">감점</th>
                            <th className="px-3 py-2 text-left border-b">수정 가이드</th>
                          </tr>
                        </thead>
                        <tbody>
                          {safeScoreDetails.a11yScoreDetails.length > 0 ? (
                            (safeScoreDetails.a11yScoreDetails as any[]).map((d: any, i: number) => (
                              <tr key={i} className="border-b last:border-b-0 hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{d.kwcagId || '-'}</td>
                                <td className="px-3 py-2">{d.name}</td>
                                <td className="px-3 py-2 text-center">
                                  {(() => {
                                    const map: Record<string, { label: string; color: string }> = {
                                      PASS:        { label: '통과',          color: '#16a34a' },
                                      FAIL:        { label: '실패',          color: '#dc2626' },
                                      NOT_CHECKED: { label: '수동 확인 필요', color: '#d97706' },
                                      INCOMPLETE:  { label: '검사 불완료',    color: '#6366f1' },
                                    };
                                    const mapped = map[d.result] ?? { label: d.result, color: '#64748b' };
                                    return <span className="font-semibold text-xs" style={{ color: mapped.color }}>{mapped.label}</span>;
                                  })()}
                                </td>
                                <td className="px-3 py-2 text-right font-bold" style={{color: (d.deduction ?? d.scoreImpact ?? 0) > 0 ? '#dc2626' : '#64748b'}}>
                                  {(d.deduction ?? d.scoreImpact) != null && (d.deduction ?? d.scoreImpact) !== 0 ? `${d.deduction ?? d.scoreImpact}점` : '-'}
                                </td>
                                <td className="px-3 py-2"><GuideCell guide={d.guide || d.reason} totalCount={d.totalCount} otherIssues={d.otherIssues} /></td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="px-3 py-6 text-center text-gray-400">
                                <Spinner size="sm" className="inline mr-2" />항목 데이터를 불러오는 중입니다.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>

                  {/* 개별 이슈 상세 목록 */}
                  {Array.isArray(filteredA11y) && filteredA11y.length > 0 && (
                    <>
                      <p className="text-sm text-gray-500">아래는 실제 주소, 요소별 세부 이슈 목록입니다. 수정 시 쿈 도움이 됩니다.</p>
                      <div className="space-y-3">
                        {filteredA11y.map(item => (
                          <A11yResultItem key={item.id} item={item} />
                        ))}
                      </div>
                    </>
                  )}

                  {/* NOT_CHECKED 수동 확인 가이드 (notCheckedGuides 우선, 없으면 manualCheckItems 폴백) */}
                  {(() => {
                    // notCheckedGuides (새 API) 우선 사용
                    const notCheckedGuides = (safeScoreDetails as any).notCheckedGuides as NotCheckedGuide[] | undefined;
                    if (notCheckedGuides && notCheckedGuides.length > 0) {
                      return (
                        <Card className="border-amber-200 bg-amber-50">
                          <CardHeader>
                            <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
                              <ClipboardCheck className="w-4 h-4" />
                              수동 확인 필요 항목 ({notCheckedGuides.length}개)
                            </CardTitle>
                            <p className="text-xs text-amber-700 mt-1">
                              아래 항목은 자동 검사가 불가능합니다. 직접 점검하여 접근성을 확보하세요.
                            </p>
                          </CardHeader>
                          <CardContent className="space-y-3 pt-0">
                            {notCheckedGuides.map((guide) => (
                              <div key={guide.ruleId} className="bg-white rounded-lg border border-amber-200 p-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-amber-900 mb-0.5">{guide.ruleId}</p>
                                    <p className="text-xs text-gray-600 mb-1">
                                      <span className="font-medium">검사 불가 사유:</span> {guide.reason}
                                    </p>
                                    <p className="text-xs text-gray-700">
                                      <span className="font-medium">확인 방법:</span> {guide.manualGuide}
                                    </p>
                                  </div>
                                  {guide.toolLink && (
                                    <a
                                      href={guide.toolLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      확인 도구
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      );
                    }
                    // 폴백: 기존 manualCheckItems (a11y 응답)
                    if (safeA11y.manualCheckItems && safeA11y.manualCheckItems.length > 0) {
                      return (
                        <Card className="border-amber-200 bg-amber-50">
                          <CardHeader>
                            <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
                              <ClipboardCheck className="w-4 h-4" />
                              수동 확인 필요 항목 ({safeA11y.manualCheckItems.length}개)
                            </CardTitle>
                            <p className="text-xs text-amber-700 mt-1">
                              아래 항목은 자동 검사가 불가능합니다. 직접 점검하여 접근성을 확보하세요.
                            </p>
                          </CardHeader>
                          <CardContent className="space-y-3 pt-0">
                            {(a11y!.manualCheckItems ?? []).map(item => (
                              <div key={item.ruleId} className="bg-white rounded-lg border border-amber-200 p-3">
                                <p className="text-xs font-semibold text-amber-900 mb-0.5">
                                  [{item.kwcagId}] {item.kwcagName}
                                </p>
                                <p className="text-xs text-gray-600 mb-1">{item.description}</p>
                                <p className="text-xs text-gray-700">
                                  <span className="font-medium">확인 방법:</span> {item.manualCheckGuide}
                                </p>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      );
                    }
                    return null;
                  })()}

                  {/* 감점 기준 안내 */}
                  {safeScoreDetails.a11yScoreDetails.length > 0 && (
                    <Card className="border-blue-100">
                      <CardContent className="pt-4 space-y-4 text-xs">
                        <div>
                          <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                            <span>📊</span> 감점 기준 안내
                          </p>
                          <div className="text-gray-500 mb-2">KWCAG 2.2 항목별 감점이 합산되어 접근성 점수(100점 기준)가 계산됩니다.</div>
                          <div className="space-y-1">
                            {(safeScoreDetails.a11yScoreDetails as DiagnoseA11yScoreDetail[])
                              .filter(d => d.result === 'FAIL' && (d.deduction ?? 0) > 0)
                              .map((d, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <span className="text-red-400 shrink-0">•</span>
                                  <span className="text-gray-600">
                                    <span className="font-medium">{d.kwcagId} {d.name}:</span>
                                    {' '}{d.deduction}점 감점
                                    {d.guide && <span className="text-gray-400 ml-1">— {(() => { const t = d.guide.replace(/\\n|\n/g, ' · '); return t.length > 50 ? t.slice(0, 50) + '…' : t; })()}</span>}
                                  </span>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                        <div className="border-t border-gray-100" />
                        <div>
                          <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                            <span>🎯</span> 점수 개선 가이드
                          </p>
                          <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="min-w-full text-xs">
                              <thead>
                                <tr className="bg-gray-50 text-gray-600">
                                  <th className="px-3 py-2 text-center w-8">#</th>
                                  <th className="px-3 py-2 text-left">KWCAG</th>
                                  <th className="px-3 py-2 text-left">항목명</th>
                                  <th className="px-3 py-2 text-left">개선 방법</th>
                                  <th className="px-3 py-2 text-center whitespace-nowrap">회복 점수</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(safeScoreDetails.a11yScoreDetails as DiagnoseA11yScoreDetail[])
                                  .filter(d => d.result === 'FAIL' && (d.deduction ?? 0) > 0)
                                  .sort((a, b) => (b.deduction ?? 0) - (a.deduction ?? 0))
                                  .map((d, i) => {
                                    const guideText = (d.guide || d.name).replace(/\\n|\n/g, ' · ');
                                    return (
                                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                                        <td className="px-3 py-2 text-center">
                                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 font-bold text-xs">{i + 1}</span>
                                        </td>
                                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap font-mono">{d.kwcagId || '-'}</td>
                                        <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">{d.name}</td>
                                        <td className="px-3 py-2 text-gray-600 max-w-xs">
                                          {guideText.length > 60 ? guideText.slice(0, 60) + '…' : guideText}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          <span className="inline-block bg-green-100 text-green-700 font-bold rounded-full px-2 py-0.5 whitespace-nowrap">+{d.deduction}점</span>
                                        </td>
                                      </tr>
                                    );
                                  })
                                }
                              </tbody>
                            </table>
                          </div>
                          <div className="bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-green-700 mt-2">
                            🏆 목표 점수: <strong>접근성 80점</strong> 이상을 권장합니다.
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>

            {/* ── SEO 탭 ── */}
            <TabsContent value="seo" className="mt-4 space-y-4">
              {seoLoading ? (
                <LoadingBlock size="2xl" />
              ) : !seo ? (
                <div className="text-center py-12 text-gray-500">데이터를 불러오는 중입니다.</div>
              ) : (
                <>
                  {/* SEO 점수 요약 */}
                  <Card className="bg-gray-50 border-gray-200">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex flex-wrap gap-6 items-center">
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">SEO 점수</p>
                          <p className="text-3xl font-bold" style={{color: scoreColor(safeScoreSeo)}}>
                            {safeScoreSeo ?? '—'}점
                          </p>
                          <p className="text-xs font-medium mt-0.5" style={{color: scoreColor(safeScoreSeo)}}>{scoreLabel(safeScoreSeo)}</p>
                        </div>
                        <div className="text-gray-300 text-2xl">=</div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">기본 점수</p>
                          <p className="text-2xl font-bold text-gray-700">100점</p>
                        </div>
                        {seoActualDeduction > 0 && (
                          <>
                            <div className="text-gray-300 text-2xl">−</div>
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">감점 합계</p>
                              <p className="text-2xl font-bold text-red-600">{seoActualDeduction}점</p>
                            </div>
                          </>
                        )}
                        <div className="ml-auto text-right">
                          <p className="text-xs text-gray-400">검사 항목</p>
                          <p className="text-lg font-bold text-gray-700">
                            {(() => {
                              const details = safeScoreDetails.seoScoreDetails as DiagnoseSeoScoreDetail[];
                              const passed = details.filter(d => d.severity === 'success' || (d.deduction ?? 0) === 0).length;
                              return details.length > 0 ? `${passed} / ${details.length} 양호` : '—';
                            })()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {(safeScoreDetails.seoScoreDetails && safeScoreDetails.seoScoreDetails.length > 0) && (() => {
                    const allSeoDetails = safeScoreDetails.seoScoreDetails as DiagnoseSeoScoreDetail[];
                    const seoItems = allSeoDetails.filter(sd => sd.category !== 'PAGE_STRUCTURE');
                    const pageStructureItems = allSeoDetails.filter(sd => sd.category === 'PAGE_STRUCTURE');
                    const failedItems = allSeoDetails.filter(sd => (sd.deduction ?? 0) > 0);

                    const labelByCode: Record<string, string> = {
                      SEO001: 'ALT 완성도',
                      SEO002: 'H1 태그',
                      SEO003: 'Google PSI 점수',
                      SEO004: '메타 설명',
                      SEO005: 'OG 이미지',
                      SEO006: '구조화 데이터 (JSON-LD)',
                      SEO007: 'WebP 이미지 최적화',
                      SEO008: 'Lazy Loading 적용률',
                    };

                    const iconByCode: Record<string, string> = {
                      SEO001: '🖼️',
                      SEO002: '🏷️',
                      SEO003: '📊',
                      SEO004: '📝',
                      SEO005: '🔗',
                      SEO006: '🗂️',
                      SEO007: '⚡',
                      SEO008: '🦥',
                    };

                    const renderSeoDetailCards = (items: DiagnoseSeoScoreDetail[]) => (
                      <div className="grid gap-3">
                        {items.map(sd => {
                          const isSuccess = sd.severity === 'success' || (sd.deduction ?? 0) === 0;
                          const isError = sd.severity === 'error';
                          const statusConfig = isSuccess
                            ? { bg: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700', label: '양호' }
                            : isError
                              ? { bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', label: '오류' }
                              : { bg: 'bg-yellow-50 border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', label: '주의' };
                          const title = labelByCode[sd.code] ?? sd.type ?? sd.code;
                          return (
                            <div key={sd.code} className={cn('border rounded-xl p-4', statusConfig.bg)}>
                              <div className="flex items-start gap-3">
                                <span className="text-xl shrink-0">{iconByCode[sd.code] ?? '🔎'}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <span className="font-semibold text-gray-800">{title}</span>
                                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', statusConfig.badge)}>
                                      {statusConfig.label}
                                    </span>
                                    {(sd.deduction ?? 0) > 0 && (
                                      <span className="text-xs text-red-600 font-bold">-{sd.deduction}점</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-700 whitespace-pre-line">{sd.message}</p>
                                  {sd.psiMessage && (
                                    <p className="text-xs text-gray-500 mt-1">Lighthouse: {sd.psiMessage}</p>
                                  )}
                                  {!isSuccess && sd.guide && (
                                    <div className="mt-2 bg-white/70 rounded-lg px-3 py-2">
                                      <p className="text-xs font-semibold text-gray-600 mb-0.5">수정 가이드</p>
                                      <p className="text-xs text-gray-700 whitespace-pre-line">{sd.guide}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );

                    return (
                      <div className="space-y-4">
                        {seoItems.length > 0 && (
                          <Card className="border-blue-200 bg-blue-50">
                            <CardHeader>
                              <CardTitle className="text-base text-blue-800 flex items-center gap-2">
                                SEO 이미지 최적화 분석
                              </CardTitle>
                              <p className="text-xs text-gray-500 mt-1">
                                ALT 완성도, Google PSI 점수, OG 이미지, 구조화 데이터, WebP 최적화, Lazy Loading, 메타 설명을 확인합니다.
                              </p>
                            </CardHeader>
                            <CardContent>
                              {renderSeoDetailCards(seoItems)}
                            </CardContent>
                          </Card>
                        )}

                        {pageStructureItems.length > 0 && (
                          <Card className="border-orange-200 bg-orange-50">
                            <CardHeader>
                              <CardTitle className="text-base text-orange-800 flex items-center gap-2">
                                페이지 구조
                              </CardTitle>
                              <p className="text-xs text-gray-500 mt-1">
                                H1 태그 등 페이지 구조 관련 항목을 확인합니다.
                              </p>
                            </CardHeader>
                            <CardContent>
                              {renderSeoDetailCards(pageStructureItems)}
                            </CardContent>
                          </Card>
                        )}

                        {failedItems.length > 0 && (
                          <Card className="border-blue-100">
                            <CardContent className="pt-4 space-y-4 text-xs">
                              <div>
                                <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                  <span>📊</span> 감점 기준 안내
                                </p>
                                <div className="text-gray-500 mb-2">감점이 있는 항목만 SEO 점수에서 차감됩니다.</div>
                                <div className="space-y-1">
                                  {failedItems.map((sd, i) => (
                                    <div key={`${sd.code}-${i}`} className="flex items-start gap-2">
                                      <span className="text-blue-400 shrink-0">•</span>
                                      <span className="text-gray-600">
                                        <span className="font-medium">{labelByCode[sd.code] ?? sd.message}:</span>
                                        {' '}{sd.deduction}점 감점
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div className="border-t border-gray-100" />

                              <div>
                                <p className="font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                  <span>🎯</span> 점수 개선 가이드
                                </p>
                                <div className="overflow-x-auto rounded-lg border border-gray-200 mb-2">
                                  <table className="min-w-full text-xs">
                                    <thead>
                                      <tr className="bg-gray-50 text-gray-600">
                                        <th className="px-3 py-2 text-center w-8">#</th>
                                        <th className="px-3 py-2 text-left">항목</th>
                                        <th className="px-3 py-2 text-left">개선 방법</th>
                                        <th className="px-3 py-2 text-center whitespace-nowrap">회복 점수</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {failedItems
                                        .slice()
                                        .sort((a, b) => b.deduction - a.deduction)
                                        .map((sd, i) => {
                                          const guideText = (sd.guide || sd.message).replace(/\\n|\n/g, ' · ');
                                          return (
                                            <tr key={`${sd.code}-guide-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                                              <td className="px-3 py-2 text-center">
                                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 font-bold text-xs">{i + 1}</span>
                                              </td>
                                              <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">
                                                {labelByCode[sd.code] ?? sd.message}
                                              </td>
                                              <td className="px-3 py-2 text-gray-600 max-w-xs">
                                                {guideText.length > 60 ? guideText.slice(0, 60) + '…' : guideText}
                                              </td>
                                              <td className="px-3 py-2 text-center">
                                                <span className="inline-block bg-green-100 text-green-700 font-bold rounded-full px-2 py-0.5 whitespace-nowrap">+{sd.deduction}점</span>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-green-700">
                                  🏆 목표 점수: <strong>SEO 80점</strong> 이상을 권장합니다.
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    );
                  })()}

                  {/* 이슈 없음 */}
                  {safeScoreDetails.seoScoreDetails.length === 0 && seoActualDeduction === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-300" />
                      SEO 이슈가 발견되지 않았습니다.
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>

          {/* ── CTA 영역: fixable이 1개 이상일 때만 표시 ── */}
          {(job.summary?.fixableByAlttext ?? 0) > 0 && (
            <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-blue-50">
              <CardContent className="pt-6">
                {claimError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{claimError}</AlertDescription>
                  </Alert>
                )}
                {fixError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{fixError}</AlertDescription>
                  </Alert>
                )}
                <div className="flex flex-col md:flex-row items-center gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 mb-1">
                      {canFix
                        ? `ALT 텍스트 자동 생성 가능 이미지 ${job.summary?.fixableByAlttext ?? 0}개`
                        : `AI가 ALT 텍스트를 자동으로 생성합니다 (${job.summary?.fixableByAlttext ?? 0}개)`}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {isAuthenticated
                        ? 'ALT 텍스트를 AI로 자동 생성하여 접근성과 SEO를 한번에 개선하세요.'
                        : '무료 플랜으로 지금 바로 ALT 텍스트를 자동 생성할 수 있습니다.'}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {isAuthenticated ? (
                      canFix && (
                        <Button onClick={() => handleFix()} disabled={fixing} size="sm">
                          {fixing ? <Spinner size="sm" className="mr-2" /> : null}
                          AI로 ALT 텍스트 생성
                        </Button>
                      )
                    ) : (
                      <>
                        <Button onClick={handleGuestFixClick} size="sm" className="bg-primary text-white">
                          무료로 개선 시작하기
                        </Button>
                        <Button onClick={handleLoginAndClaim} variant="outline" size="sm">
                          로그인
                        </Button>
                      </>
                    )}
                    {canClaim && (
                      <Button onClick={() => handleClaim()} variant="outline" size="sm" disabled={claiming}>
                        {claiming ? <Spinner size="sm" className="mr-2" /> : null}
                        내 계정에 저장
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── 비회원 전환 모달 ── */}
          {showGuestModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowGuestModal(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">AI가 이미지를 지금 바로 개선합니다</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    무료 플랜으로 ALT 텍스트를 즉시 생성합니다.<br />
                    회원가입 후 진단 결과가 자동으로 연동됩니다.
                  </p>
                </div>
                <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
                  <p className="text-sm font-semibold text-primary">무료 플랜 · {freePlanLimit}회 제공</p>
                  <ul className="mt-2 space-y-1">
                    {['이미지 ALT 자동 생성', 'KWCAG 2.2 준수 텍스트', 'SEO 최적화 반영'].map(item => (
                      <li key={item} className="text-xs text-gray-600 flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-2">
                  <Button className="w-full" onClick={handleSignupAndFix}>
                    무료 회원가입 후 개선 시작
                  </Button>
                  <Button variant="outline" className="w-full" onClick={handleLoginAndClaim}>
                    이미 회원이에요 → 로그인
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Tip */}
          <div className="bg-blue-50 rounded-2xl border border-blue-100 px-6 py-4 flex gap-3 mt-8">
            <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800 mb-0.5">진단 가능한 사이트</p>
              <p className="text-xs text-blue-700">
                공개된 웹사이트(HTTP/HTTPS)이며 robots.txt에서 크롤링을 허용하는 경우 진단이 가능합니다.
                로그인이 필요한 페이지나 내부망 사이트는 진단이 제한될 수 있습니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  } // ← renderResult 함수 닫힘

  // ─── Render: Failed ──────────────────────────────────────────────────────────
  const renderFailed = () => {
    const errMsg = resolveErrorMessage(job?.message, job?.errorCode);
    const isRateLimit = job?.errorCode === 'RATE_LIMIT_EXCEEDED' || job?.message === 'RATE_LIMIT_EXCEEDED'
      || job?.errorCode === 'GLOBAL_RATE_LIMIT_EXCEEDED' || job?.message === 'GLOBAL_RATE_LIMIT_EXCEEDED';
    const isAccessDenied = job?.errorCode === 'URL_ACCESS_DENIED' || job?.message === 'URL_ACCESS_DENIED';

    return (
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
        <div className="max-w-lg w-full">
          {/* 카드 */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            {/* 상단 색상 바 */}
            <div className={cn(
              'h-1.5 w-full',
              isRateLimit ? 'bg-amber-400' : 'bg-red-400'
            )} />

            <div className="p-8">
              {/* 아이콘 + 제목 */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className={cn(
                  'w-16 h-16 rounded-full flex items-center justify-center mb-4',
                  isRateLimit ? 'bg-amber-50' : 'bg-red-50'
                )}>
                  {isRateLimit
                    ? <Timer className={cn('w-8 h-8', 'text-amber-500')} />
                    : <XCircle className="w-8 h-8 text-red-400" />
                  }
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">진단에 실패했습니다</h2>
                <p className="text-sm text-gray-500 leading-relaxed max-w-sm">{errMsg}</p>
              </div>

              {/* 실패한 URL 표시 */}
              {job?.targetUrl && (
                <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 mb-6 border border-gray-100">
                  <Globe className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="text-xs text-gray-500 truncate font-mono">{job.targetUrl}</span>
                </div>
              )}

              {/* 원인 안내 */}
              {isAccessDenied && (
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 text-xs text-amber-800 space-y-1">
                  <p className="font-semibold">다음 사항을 확인해 주세요</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                    <li>URL이 공개적으로 접근 가능한지 확인</li>
                    <li>robots.txt에서 크롤링을 허용하는지 확인</li>
                    <li>로그인이 필요한 페이지가 아닌지 확인</li>
                  </ul>
                </div>
              )}

              {/* 버튼 */}
              <div className="flex flex-col sm:flex-row gap-3">
                {job?.targetUrl && !isRateLimit && (
                  <Button
                    className="flex-1"
                    onClick={() => {
                      const url = job.targetUrl;
                      setPhase('input');
                      setJob(null);
                      setJobId(null);
                      setSearchParams({});
                      setUrlInput(url);
                      setTimeout(() => {
                        setSubmitError(null);
                        setPhase('loading');
                        setImages([]);
                        setA11y(null);
                        setSeo(null);
                        setActiveTab('images');
                        diagnoseApi.start(url).then(res => {
                          if (res.errorCode) {
                            setPhase('input');
                            setSubmitError(res.message || getErrorMessage(res.errorCode));
                            return;
                          }
                          setJobId(res.jobId);
                          setSearchParams({ jobId: res.jobId });
                          if (res.guestToken) {
                            sessionStorage.setItem(GT_KEY(res.jobId), res.guestToken);
                          }
                        }).catch((err: any) => {
                          setPhase('input');
                          const errorCode = err.response?.data?.errorCode;
                          const msg = errorCode
                            ? (err.response?.data?.message || getErrorMessage(errorCode))
                            : (err.response?.data?.message || '진단 요청에 실패했습니다.');
                          setSubmitError(msg);
                        });
                      }, 0);
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    다시 시도
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setPhase('input'); setJob(null); setJobId(null); setSearchParams({}); }}
                >
                  다른 URL 진단하기
                </Button>
              </div>
            </div>
          </div>

          {/* 하단 안내 */}
          <p className="text-center text-xs text-gray-400 mt-4">
            문제가 반복되면 <a href="/inquiries" className="text-blue-500 hover:underline">고객센터</a>로 문의해 주세요.
          </p>
        </div>
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────────────
  return (
    <HelmetProvider>
      <Helmet>
        <title>무료 접근성 & SEO 진단 | ALT Generation</title>
        <meta name="description" content="KWCAG 2.2 기준 핵심 9개 항목 접근성 자동 진단 + AI 이미지 ALT 품질 평가 + SEO 분석. 무료로 시작하세요." />
        <meta name="keywords" content="접근성 진단, SEO 진단, 웹 접근성, KWCAG 2.2, ALT 텍스트, 무료 진단, AI 접근성, 웹사이트 분석" />
        <link rel="canonical" href="https://alt.aigeneration-tech.com/diagnose" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://alt.aigeneration-tech.com/diagnose" />
        <meta property="og:title" content="무료 접근성 & SEO 진단 | ALT Generation" />
        <meta property="og:description" content="KWCAG 2.2 기준 핵심 9개 항목 접근성 자동 진단 + AI 이미지 ALT 품질 평가 + SEO 분석. 무료로 시작하세요." />
        <meta property="og:image" content="https://alt.aigeneration-tech.com/og-diagnose.png" />
        <meta property="og:site_name" content="ALT Generation" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="무료 접근성 & SEO 진단 | ALT Generation" />
        <meta name="twitter:description" content="KWCAG 2.2 기준 핵심 9개 항목 접근성 자동 진단 + AI 이미지 ALT 품질 평가 + SEO 분석. 무료로 시작하세요." />
        <meta name="twitter:image" content="https://alt.aigeneration-tech.com/og-diagnose.png" />
        
        {/* 구조화된 데이터 (JSON-LD) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "무료 접근성 & SEO 진단",
            "url": "https://alt.aigeneration-tech.com/diagnose",
            "description": "KWCAG 2.2 기준 핵심 9개 항목 접근성 자동 진단 + AI 이미지 ALT 품질 평가 + SEO 분석. 무료로 시작하세요.",
            "applicationCategory": "UtilityApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "KRW"
            },
            "featureList": [
              "KWCAG 2.2 접근성 진단",
              "AI 이미지 ALT 품질 평가",
              "SEO 분석",
              "무료 진단"
            ]
          })}
        </script>
      </Helmet>
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        {phase === 'input' && renderInput()}
        {phase === 'loading' && renderLoading()}
        {phase === 'done' && renderResult()}
        {phase === 'failed' && renderFailed()}
        {phase === 'input' && <Footer />}
      </div>
    </HelmetProvider>
  );
}
